import { EMOTIONS, EMOTION_META, STANCE_META, TARGET_META, POLARITY_META, SIGNALS, SIGNAL_META, signalMeta, orderSignals } from '../../../constants/sentiment';
import { formatUTCDate } from '../../../utils/dates';
import { REPLY_FILTERS } from './useVoices';
import { useId, useRef, useState } from 'react';
import { Button } from '../../ui';
import { describeContextError, useReviewHeartbeatContext } from './useHeartbeatContext';

/**
 * How one reply reads in the evidence drawer, and the drawer's filter and sort
 * options. Shared by the account page and the run page - both open the same
 * drawer over the same data, and a second copy of the legend is exactly the
 * kind of thing that drifts.
 */

/** REPLY_FILTERS with the emotion options filled in from the taxonomy. */
export const replyFilters = REPLY_FILTERS.map((f) => (
	f.key === 'emotion'
		? { ...f, options: EMOTIONS.map((e) => ({ value: e, label: EMOTION_META[e].label })) }
		: f
));

/**
 * "Is anyone threatening to sue" is the question a manager opens the product
 * to ask, so it is a filter, not something to be found by reading. "Anything
 * flagged" first because most days that is the only one they want.
 */
export const signalFilterOptions = [
	{ value: 'any', label: 'Anything flagged' },
	...SIGNALS.map((g) => ({ value: g, label: SIGNAL_META[g].label })),
];

export const replySortOptions = [
	{ value: 'recent', label: 'Newest' },
	{ value: 'likes', label: 'Most liked' },
];

/**
 * Stance first - it is the badge, not a footnote. A reply labelled before
 * stance existed falls back to its polarity so an unrelabelled run still
 * reads as something rather than "Unlabeled".
 */
export function Reply({ r, accountId, runId, readingRevision, historical = false, reviewActive = false }) {
	const unclear = r.label?.interpretationStatus === 'needs_context' || r.label?.stance === 'unclear';
	const meta = EMOTION_META[r.label?.emotion];
	const stance = !unclear && r.label?.stance ? STANCE_META[r.label.stance] : null;
	const target = r.label?.target && r.label.target !== 'none' ? TARGET_META[r.label.target]?.label : null;
	const fallback = !unclear && !stance && r.label?.polarity ? POLARITY_META[r.label.polarity]?.label : null;
	const postedAt = formatUTCDate(r.postedAt);
	// Signals lead the row: a legal threat outranks how the person felt.
	const signals = orderSignals(r.label?.signals || []);
	return (
		<div>
			{signals.length > 0 && (
				<div className="mb-1.5 flex flex-wrap gap-1">
					{signals.map((g) => {
						const m = signalMeta(g);
						return (
							<span key={g} className={`rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${m.chip}`} title={m.hint}>
								{m.label}
							</span>
						);
					})}
				</div>
			)}
			<p className="text-sm text-foreground">{r.text}</p>
			<div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
				{unclear && <span className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">Unclear</span>}
				{stance && (
					<span className={`rounded-full px-2 py-0.5 font-medium ${stance.soft}`}>{stance.label}</span>
				)}
				{!stance && fallback && <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{fallback}</span>}
				{meta && <span className="inline-flex items-center gap-1">
					<span className={`inline-block h-2 w-2 rounded-sm ${meta.bg}`} />{meta.label}
				</span>}
				{target && <span>· {target.toLowerCase()}</span>}
				{typeof r.likes === 'number' && <span>· {r.likes} likes</span>}
				{postedAt && <span>· {postedAt}</span>}
			</div>
			{unclear && r.label?.contextReason && <p className="mt-2 text-xs text-muted-foreground">{r.label.contextReason}</p>}
			{accountId && runId && readingRevision && !historical && (r.replyId || r._id || r.id) && <ReplyCorrection key={`${r.replyId || r._id || r.id}-${readingRevision}`} replyId={r.replyId || r._id || r.id} accountId={accountId} runId={runId} readingRevision={readingRevision} disabled={reviewActive} />}
		</div>
	);
}

function ReplyCorrection({ replyId, accountId, runId, readingRevision, disabled }) {
	const [open, setOpen] = useState(false);
	const [stance, setStance] = useState('');
	const [reason, setReason] = useState('');
	const lock = useRef(false);
	const id = useId();
	const correction = useReviewHeartbeatContext(accountId, runId, true);
	if (correction.isSuccess && correction.data?.status !== 'failed') return <p role="status" className="mt-2 text-sm text-teal-700">{correction.data?.status === 'ready' ? 'Correction saved.' : 'Correction queued.'} It applies only to this reply.</p>;
	return <div className="mt-3">
		{!open ? <Button variant="link" size="sm" className="h-auto p-0 text-xs" disabled={disabled} onClick={() => setOpen(true)}>This reading looks wrong</Button> : <form className="space-y-3 rounded-lg bg-muted p-3" onSubmit={async (e) => {
			e.preventDefault();
			if (lock.current || disabled || !stance || !reason.trim()) return;
			lock.current = true;
			try { await correction.mutateAsync({ replyId, stance, reason: reason.trim(), expectedRevision: readingRevision }); } catch (_) { /* Retain the draft. */ } finally { lock.current = false; }
		}}>
			<p className="text-xs text-muted-foreground">Correct this reply only. This will not create a general rule about a person or relationship.</p>
			<label className="block text-sm" htmlFor={`${id}-stance`}>Correct standing<select id={`${id}-stance`} value={stance} disabled={disabled || correction.isPending} onChange={(e) => setStance(e.target.value)} className="mt-1 w-full rounded-lg border border-input bg-card p-2"><option value="">Choose a standing</option><option value="for">Support</option><option value="against">Opposition</option><option value="none">No position</option><option value="unclear">Unclear</option></select></label>
			<label className="block text-sm" htmlFor={`${id}-reason`}>Reason for this correction<textarea id={`${id}-reason`} rows={2} maxLength={600} value={reason} disabled={disabled || correction.isPending} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded-lg border border-input p-2" /></label>
			{correction.error && <p role="alert" className="text-sm text-rose-700">{describeContextError(correction.error, true)}</p>}
			{correction.isSuccess && correction.data?.status === 'failed' && <p role="alert" className="text-sm text-rose-700">The correction could not finish. Your previous reading and draft are still available. Please try again.</p>}
			<div className="flex flex-wrap gap-2"><Button size="sm" type="submit" disabled={disabled || correction.isPending || !stance || !reason.trim()}>Correct this reply</Button><Button size="sm" type="button" variant="ghost" disabled={correction.isPending} onClick={() => setOpen(false)}>Cancel</Button></div>
		</form>}
	</div>;
}
